import type { ReactNode } from 'react'
import { BrowserRouter, Navigate, Outlet, Route, Routes, useLocation } from 'react-router-dom'

import { CHANGE_PASSWORD_PATH, LOGIN_PATH, NAV_LEAVES, fromPath, loginPathFor } from './lib/nav'
import { SessionProvider, useSession } from './lib/SessionContext'
import AdminLogin from './routes/AdminLogin'
import Approvals from './routes/approvals/Approvals'
import ChangePassword from './routes/ChangePassword'
import Employees from './routes/employees/Employees'
import Home from './routes/Home'
import MasterBranches from './routes/master/Branches'
import MasterFunctions from './routes/master/Functions'
import MasterBu from './routes/master/Bu'
import MasterCompanies from './routes/master/Companies'
import MasterHolidays from './routes/master/Holidays'
import MasterShifts from './routes/master/Shifts'
import MasterLeaveQuota from './routes/master/LeaveQuota'
import MasterLeaveTypes from './routes/master/LeaveTypes'
import ShiftPlan from './routes/master/ShiftPlan'
import ShiftDay from './routes/master/ShiftDay'
import MasterDepartments from './routes/master/Departments'
import MyLeave from './routes/me/MyLeave'
import MyOt from './routes/me/MyOt'
import MyWorktime from './routes/me/MyWorktime'
import MyApprovals from './routes/me/MyApprovals'
import MyTeam from './routes/me/MyTeam'
import AdminLine from './routes/line/AdminLine'
import Admins from './routes/admins/Admins'
import Delegate from './routes/admins/Delegate'
import News from './routes/news/News'
import Slides from './routes/slides/Slides'
import OffsiteCheckins from './routes/offsite/OffsiteCheckins'
import OffsitePermissions from './routes/offsite/OffsitePermissions'
import OffsiteSites from './routes/offsite/OffsiteSites'
import OffsiteWindows from './routes/offsite/OffsiteWindows'
import PersonReport from './routes/reports/PersonReport'
import AttendanceDay from './routes/reports/AttendanceDay'
import OffsiteSummary from './routes/reports/OffsiteSummary'
import OtSummary from './routes/reports/OtSummary'
import TaData from './routes/reports/TaData'
import WorkSummary from './routes/reports/WorkSummary'
import Placeholder, { NotFound } from './routes/Placeholder'
import Shell from './routes/Shell'
import UserLogin from './routes/UserLogin'
import WalkrunDashboard from './routes/dashboard/WalkrunDashboard'
import Walkrun from './routes/Walkrun'

/** ระหว่างรอ /api/auth/me รอบแรก ยังไม่รู้ว่าล็อกอินอยู่ไหม — ห้ามวาดทั้งฟอร์ม login ทั้งเนื้อหา */
function Booting() {
  return <p className="booting">กำลังตรวจสอบสิทธิ์…</p>
}

/**
 * ประตูของทุกหน้าที่ต้องล็อกอิน
 *
 * ที่ต้องอยู่**เหนือ** Shell เพราะเวลา session หลุด (401 ระหว่างใช้งาน) ตัวนี้จะ
 * render <Navigate/> แทน <Outlet/> ทำให้หน้าลูกถอดออกไปพร้อมกัน — ถ้าเช็คในหน้าลูก
 * หน้าลูกจะ render ต่อด้วย session ที่หายไปแล้วก่อนถูกเด้ง
 */
function RequireAuth() {
  const { state, lastKind } = useSession()
  const location = useLocation()

  if (state.status === 'loading') return <Booting />

  if (state.status === 'signed-out') {
    return (
      <Navigate
        to={loginPathFor(location.pathname, lastKind)}
        state={{ from: location.pathname + location.search }}
        replace
      />
    )
  }

  const { session } = state

  // แอดมินที่ยังใช้รหัสที่ผู้ดูแลระบบตั้งให้ — ล็อกไว้ที่หน้าเปลี่ยนรหัสจนกว่าจะเปลี่ยน
  if (session.kind === 'admin' && session.mustChangePassword) {
    if (location.pathname !== CHANGE_PASSWORD_PATH) {
      return <Navigate to={CHANGE_PASSWORD_PATH} replace />
    }
  }

  return <Outlet />
}

/** หน้า login: คนที่ล็อกอินอยู่แล้วไม่ควรเห็น — พาไปปลายทางที่ตั้งใจจะไปตั้งแต่แรก */
function PublicOnly({ children }: { children: ReactNode }) {
  const { state } = useSession()
  const location = useLocation()

  if (state.status === 'loading') return <Booting />
  // จุดเดียวที่ตัดสินใจว่าล็อกอินเสร็จแล้วไปไหน (ฟอร์ม login จึงไม่ต้อง navigate เอง)
  if (state.status === 'signed-in') return <Navigate to={fromPath(location.state)} replace />

  return <>{children}</>
}

export default function App() {
  return (
    <SessionProvider>
      {/* BrowserRouter ได้เพราะ IIS มี rewrite ทุก path → index.html อยู่แล้ว (public/web.config) */}
      <BrowserRouter>
        <Routes>
          <Route
            path={LOGIN_PATH.admin}
            element={
              <PublicOnly>
                <AdminLogin />
              </PublicOnly>
            }
          />
          <Route
            path={LOGIN_PATH.user}
            element={
              <PublicOnly>
                <UserLogin />
              </PublicOnly>
            }
          />

          {/* Walk & Run dashboard — พนักงานเปิดจากแบนเนอร์บน rich menu ผ่าน LIFF ตัวแยก
              ⚠️ ต้องอยู่ "นอก" RequireAuth/Shell: ยืนยันตัวตนด้วย idToken ของ LINE
                 ไม่ใช่ cookie session ของผู้ดูแล ถ้าเอาไปไว้ข้างในพนักงานจะถูกเด้ง
                 ไปหน้า login ของหลังบ้านทันที */}
          <Route path="/dashboard/walkrun" element={<WalkrunDashboard />} />

          <Route element={<RequireAuth />}>
            <Route element={<Shell />}>
              <Route path="/" element={<Home />} />
              <Route path={CHANGE_PASSWORD_PATH} element={<ChangePassword />} />

              {/* เข้างานนอกสถานที่ — path ต้องตรงกับที่ลงไว้ใน NAV เป๊ะ ๆ
                  เพราะ Shell เช็คสิทธิ์ด้วย canAccess() ที่เทียบ path แบบตรงตัว
                  (path ที่ไม่อยู่ใน NAV จะกลายเป็นหน้าที่ทุกคนเข้าได้) */}
              <Route path="/admin/offsite/sites" element={<OffsiteSites />} />
              <Route path="/admin/offsite/permissions" element={<OffsitePermissions />} />
              <Route path="/admin/offsite/windows" element={<OffsiteWindows />} />
              <Route path="/admin/offsite/checkins" element={<OffsiteCheckins />} />

              <Route path="/admin/employees" element={<Employees />} />
              <Route path="/admin/shift-plan" element={<ShiftPlan />} />
              <Route path="/admin/shift-day" element={<ShiftDay />} />

              <Route path="/admin/master/companies" element={<MasterCompanies />} />
              <Route path="/admin/master/departments" element={<MasterDepartments />} />
              <Route path="/admin/master/branches" element={<MasterBranches />} />
              <Route path="/admin/master/functions" element={<MasterFunctions />} />
              <Route path="/admin/master/bu" element={<MasterBu />} />
              <Route path="/admin/master/holidays" element={<MasterHolidays />} />
              <Route path="/admin/master/shifts" element={<MasterShifts />} />
              <Route path="/admin/master/leave-types" element={<MasterLeaveTypes />} />
              <Route path="/admin/master/leave-quota" element={<MasterLeaveQuota />} />

              {/* คิวอนุมัติของ HR — เขียนลง HR_LEAVE/HR_EMP_LEAVE ของ production
                  (ดูคำเตือนที่หัว backend/src/lib/approvals.js) */}
              <Route path="/admin/approvals" element={<Approvals />} />

              <Route path="/admin/reports/person" element={<PersonReport />} />
              <Route path="/admin/reports/ta-data" element={<TaData />} />
              <Route path="/admin/reports/attendance-day" element={<AttendanceDay />} />
              {/* หน้า "ของฉัน" — พนักงานดูข้อมูลตัวเอง (backend/src/routes/me.js)
                  empCode มาจาก session ไม่ใช่ URL จึงไม่ต้องมี guard เพิ่มนอกจาก nav */}
              <Route path="/me/worktime" element={<MyWorktime />} />
              <Route path="/me/leave" element={<MyLeave />} />
              <Route path="/me/ot" element={<MyOt />} />
              <Route path="/me/approvals" element={<MyApprovals />} />
              {/* URL สั้นที่คนพิมพ์/จำผิดบ่อย — เด้งไปตัวจริงแทนที่จะขึ้น "ไม่พบหน้านี้"
                  (ตัวจริงยังถูกคุมสิทธิ์ด้วย NAV เหมือนเดิม redirect ไม่ได้เปิดช่องอะไร) */}
              <Route path="/me/approve" element={<Navigate to="/me/approvals" replace />} />
              <Route path="/me/team" element={<MyTeam />} />

              <Route path="/admin/reports/ot" element={<OtSummary />} />
              <Route path="/admin/reports/offsite" element={<OffsiteSummary />} />
              {/* ประวัติใบลา/ใบปรับปรุงเวลา = หน้าอนุมัติหน้าเดิม เปิดมาที่สถานะ "อนุมัติแล้ว" */}
              <Route path="/admin/approvals/history" element={<Approvals />} />
              <Route path="/admin/reports/worktime" element={<WorkSummary />} />

              <Route path="/admin/line" element={<AdminLine />} />

              <Route path="/admin/admins" element={<Admins />} />
              <Route path="/admin/delegate" element={<Delegate />} />
              <Route path="/admin/news" element={<News />} />
              <Route path="/admin/slides" element={<Slides />} />

              <Route path="/admin/walkrun" element={<Walkrun />} />

              {/* เมนูที่ยังไม่มีของจริง — สร้างจากตารางเดียวกับที่วาดแถบเมนู
                  จะได้ไม่มีเมนูที่กดแล้ว 404 หรือ route ที่ไม่มีใครเข้าถึง
                  สิทธิ์เข้าหน้าไหนได้ Shell เช็คด้วย canAccess() จากตารางเดียวกัน

                  NAV_LEAVES ไม่ใช่ NAV — ตัวนี้คลี่เมนูย่อยออกมาด้วย ถ้าใช้ NAV
                  เมนูในหมวดจะไม่มี route แล้วกดจากแถบเมนูตัวเองแล้วเจอ "ไม่พบหน้านี้" */}
              {NAV_LEAVES.filter((item) => item.placeholder).map((item) => (
                <Route
                  key={item.path}
                  path={item.path}
                  element={<Placeholder title={item.label} note={item.note} />}
                />
              ))}

              <Route path="*" element={<NotFound />} />
            </Route>
          </Route>
        </Routes>
      </BrowserRouter>
    </SessionProvider>
  )
}
